import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { GizmoHelper, GizmoViewport } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as THREE from 'three';

import { EditDrawingButton } from '../../components/drawings/EditDrawingButton.tsx';
import { FileDownloadProgress } from '../../components/loading/FileDownloadProgress.tsx';
import { AppliedMarketMatchBadge } from '../../components/workspace/AppliedMarketMatchBadge.tsx';
import AutoPositionSidebar from '../../components/workspace/AutoPositionSidebar.tsx';
import { BakedRenderMarketMatchNotice } from '../../components/workspace/BakedRenderMarketMatchNotice.tsx';
import { BottomToolbar } from '../../components/workspace/BottomToolbar.tsx';
import { ViewModeTransitionOverlay } from '../../components/workspace/ViewModeTransitionOverlay.tsx';
import { useArrowKeyMovement } from '../../components/workspace/hooks/useArrowKeyMovement.ts';
import { ViewModeSwitch } from '../../components/workspace/panel/ViewModeSwitch.tsx';
import { WorkspaceSidePanel } from '../../components/workspace/panel/WorkspaceSidePanel.tsx';
import { isPanelTab } from '../../components/workspace/panel/panelUrl.ts';
import { CameraController } from '../../components/workspace/scene/CameraController.tsx';
import { CoordinateSystem } from '../../components/workspace/scene/CoordinateSystem.tsx';
import CustomGrid from '../../components/workspace/scene/CustomGrid.tsx';
import { FrontendMeasurementLoadingOverlay } from '../../components/workspace/scene/FrontendMeasurementLoadingOverlay.tsx';
import { FrontendMeasurementRunner } from '../../components/workspace/scene/FrontendMeasurementRunner.tsx';
import { type LampPopupData, LampQuickEditPopup } from '../../components/workspace/scene/LampQuickEditPopup.tsx';
import { type LayerPopupData, LayerQuickEditPopup } from '../../components/workspace/scene/LayerQuickEditPopup.tsx';
import { Lighting } from '../../components/workspace/scene/Lighting.tsx';
import { MeasurementScale } from '../../components/workspace/scene/MeasurementScale.tsx';
import { Measurements } from '../../components/workspace/scene/Measurements.tsx';
import { ResultLayerPopup } from '../../components/workspace/scene/ResultLayerPopup.tsx';
import { Scene } from '../../components/workspace/scene/Scene.tsx';
import { SceneInteractionHandler } from '../../components/workspace/scene/SceneInteractionHandler.tsx';
import { MeasurementExclusionEditor } from '../../components/workspace/scene/measurementPlane/MeasurementExclusionEditor.tsx';
import { MeasurementPlaneEditor } from '../../components/workspace/scene/measurementPlane/MeasurementPlaneEditor.tsx';
import { MeasurementPlanePanel } from '../../components/workspace/scene/measurementPlane/MeasurementPlanePanel.tsx';
import { MeasurementPlaneResults } from '../../components/workspace/scene/measurementPlane/MeasurementPlaneResults.tsx';
import { MeasurementPlaneRunner } from '../../components/workspace/scene/measurementPlane/MeasurementPlaneRunner.tsx';
import { MeasurementPlaneVertexEditor } from '../../components/workspace/scene/measurementPlane/MeasurementPlaneVertexEditor.tsx';
import { MeasurementPlanesRenderer } from '../../components/workspace/scene/measurementPlane/MeasurementPlanesRenderer.tsx';
import { MeasurementPointsSwitch } from '../../components/workspace/scene/measurementPlane/MeasurementPointsSwitch.tsx';
import { NormPromptPopup } from '../../components/workspace/scene/measurementPlane/NormPromptPopup.tsx';
import {
  CameraBridge,
  RealTimeRenderOverlay,
} from '../../components/workspace/scene/realtime/RealTimeRenderOverlay.tsx';
import { useCameraSnapshotRef } from '../../components/workspace/scene/realtime/useCameraSnapshotRef.ts';
import { LeftToolbar } from '../../components/workspace/sidebar/LeftToolbar.tsx';
import { Sidebar } from '../../components/workspace/sidebar/Sidebar.tsx';
import { ExportProvider } from '../../context/ExportContext.tsx';
import { FrontendReportImagesProvider } from '../../context/FrontendReportImagesContext.tsx';
import { PdfCacheProvider } from '../../context/PdfCacheContext.tsx';
import { PdfExportProvider } from '../../context/PdfExportContext.tsx';
import { ViewModeTransitionProvider } from '../../context/ViewModeTransitionContext.tsx';
import { useDrawingsEnabled } from '../../hooks/useDrawingsEnabled.ts';
import {
  MeasurementData,
  loadProject,
  loadWireframeFiles,
  setCameraInitialized,
  setMeasurementSource,
  startAutoPositioning,
  startPeriodicChecking,
  startTamanOptimization,
  stopPeriodicChecking,
  switchActiveVersion,
  triggerClearWorkspaceState,
} from '../../store/reducers/workspace.reducer.ts';
import { getUserFeatureFlags } from '../../store/selectors/featureFlags.selector.ts';
import { getNorms } from '../../store/selectors/user.selector.ts';
import type { RootState } from '../../store/store.ts';
import { history } from '../../store/store.ts';
import { greyP4 } from '../../theme.ts';
import { ModelLayer } from '../../types/model.ts';
import { Version } from '../../types/project.ts';
import {
  FRONTEND_PDF_PLOTS_FLAG,
  MEASUREMENT_SURFACES_FLAG,
  WORKSPACE_LAYOUT_V2_FLAG,
} from '../../utils/featureFlagKeys';
import {
  canExportPdf,
  isAutopositioningInProgress as checkIsAutopositioningInProgress,
  isCalculationCompleted as checkIsCalculationCompleted,
  isCalculationInProgress as checkIsCalculationInProgress,
} from '../../utils/jobStatus.ts';
import { isFullyCatalogueAssigned } from '../../utils/lampMarketMatch.ts';
import {
  selectWorkspaceMeasurementData,
  shouldUseFrontendMeasurementSource,
} from '../../utils/measurementCalculation/measurementSource.ts';
import { requestFrontendMeasurementCalculation } from '../../utils/measurementCalculation/trigger.ts';
import { useStore } from './hooks.ts';

// Lazy load NewExportView since it's only used when activeTab is 'exports'
const NewExportView = React.lazy(() =>
  import('../../components/workspace/ExportView.tsx').then((module) => ({ default: module.ExportView })),
);

THREE.Object3D.DEFAULT_UP.set(0, 0, 1);

/** Stable stand-in while measurement results are not there yet (see the measurement view below). */
const NO_MEASUREMENT_DATA: MeasurementData[] = [];

export const WorkspacePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = Number(searchParams.get('projectId'));
  const versionId = Number(searchParams.get('versionId'));
  const tabParam = searchParams.get('tab') as 'process' | 'exports' | null;
  const activeTab: 'process' | 'exports' = tabParam === 'exports' ? 'exports' : 'process';
  const {
    showMeasurements,
    project,
    activeVersion,
    uploadedModels,
    fileDownloadProgress,
    isLoadingFiles,
    activeViewModes,
    showGrid,
    showCompass,
    modelLayers,
    setModelLayers,
    showAutoPositionSidebar,
    setShowAutoPositionSidebar,
    showIESModels,
    isOrthographicView,
    setOrthographicView,
  } = useStore();
  const frontendMeasurementData = useSelector((s: RootState) => s.workspace.frontendMeasurementData);
  const appliedMarketMatch = useSelector((s: RootState) => s.workspace.appliedLampSimilaritySelection);
  const useFrontendMeasurement = useSelector((s: RootState) =>
    shouldUseFrontendMeasurementSource(s.featureFlags.userFeatureFlags, activeVersion),
  );
  const frontendMeasurementCalculating = useSelector((s: RootState) => s.workspace.frontendMeasurementCalculating);
  const measurementData = useSelector(selectWorkspaceMeasurementData);
  const norms = useSelector(getNorms);
  // Measurement surfaces (draw/cut/illuminance) are gated behind a per-user flag.
  const measurementSurfacesEnabled = useSelector((s: RootState) =>
    s.featureFlags.userFeatureFlags.includes(MEASUREMENT_SURFACES_FLAG),
  );
  const planeCount = useSelector((s: RootState) => s.measurementPlanes.planes.length);
  const drawingsEnabled = useDrawingsEnabled();
  const pointsView = useSelector((s: RootState) => s.measurementPlanes.pointsView);
  const planeDrawActive = useSelector(
    (s: RootState) => s.measurementPlanes.draw.active || s.measurementPlanes.exclusionDraw.active,
  );
  // Whole-model measurement points and drawn-surface points are mutually exclusive.
  // With no surfaces (or the switch on "model"), the model measurement shows as before.
  // While drawing a surface, hide the model points too (clean canvas to draw on).
  const showModelMeasurement = (planeCount === 0 || pointsView === 'model') && !planeDrawActive;
  // Capability: ALL lamps are catalogue-assigned → frontend measurement can run.
  const fullyAssigned = isFullyCatalogueAssigned(activeVersion);
  const frontendPdfPlotsEnabled = useSelector((s: RootState) =>
    getUserFeatureFlags(s).includes(FRONTEND_PDF_PLOTS_FLAG),
  );
  const pdfExportEnabled = canExportPdf(activeVersion, { frontendPdfPlotsEnabled });
  // New side panel (section rail + fixed next-step footer); without the flag the legacy sidebar stays.
  const layoutV2 = useSelector((s: RootState) => getUserFeatureFlags(s).includes(WORKSPACE_LAYOUT_V2_FLAG));

  const [showExportsPanel, setShowExportsPanel] = useState(false);
  const exportVisible = showExportsPanel || activeTab === 'exports';
  const needsFrontendMeasurementCanvas = fullyAssigned && pdfExportEnabled;
  const showProcessView = !exportVisible;
  const mountMeasurementCanvas = showProcessView || (exportVisible && needsFrontendMeasurementCanvas);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Arrow key movement for selected lamp - disabled when exports panel is showing or not on process tab
  useArrowKeyMovement(activeTab === 'process' && !showExportsPanel);

  // Quick-edit popup state for layers & lamps
  const [layerPopup, setLayerPopup] = useState<LayerPopupData | null>(null);
  const [lampPopup, setLampPopup] = useState<LampPopupData | null>(null);

  useEffect(() => {
    const handleLayerQuickEdit = (e: Event) => {
      const detail = (e as CustomEvent).detail as LayerPopupData;
      setLampPopup(null);
      setLayerPopup(detail);
    };
    const handleLampQuickEdit = (e: Event) => {
      const detail = (e as CustomEvent).detail as LampPopupData;
      setLayerPopup(null);
      setLampPopup(detail);
    };
    window.addEventListener('layer-quick-edit', handleLayerQuickEdit);
    window.addEventListener('lamp-quick-edit', handleLampQuickEdit);
    return () => {
      window.removeEventListener('layer-quick-edit', handleLayerQuickEdit);
      window.removeEventListener('lamp-quick-edit', handleLampQuickEdit);
    };
  }, []);

  const dispatch = useDispatch();

  // Orbit cursor indicator — white circle follows cursor during any camera drag
  const [orbitCursor, setOrbitCursor] = useState<{ x: number; y: number } | null>(null);
  const mouseBtnDown = useRef(false);
  const dragDownPos = useRef<{ x: number; y: number } | null>(null);
  const dragTravel = useRef(0);
  const ORBIT_DRAG_THRESHOLD = 5;

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      // Only track camera-drag feedback when the gesture starts on the 3D canvas,
      // not on overlays (sidebar, toolbars, modals) that sit above it.
      if (!(e.target instanceof HTMLCanvasElement)) return;
      if (e.button === 0 || e.button === 2) {
        mouseBtnDown.current = true;
        dragDownPos.current = { x: e.clientX, y: e.clientY };
        dragTravel.current = 0;
      }
    };
    const onMove = (e: PointerEvent) => {
      if (!mouseBtnDown.current || !dragDownPos.current) return;
      const dx = e.clientX - dragDownPos.current.x;
      const dy = e.clientY - dragDownPos.current.y;
      dragTravel.current = Math.sqrt(dx * dx + dy * dy);
      if (dragTravel.current > ORBIT_DRAG_THRESHOLD) {
        setOrbitCursor({ x: e.clientX, y: e.clientY });
      }
    };
    const onUp = () => {
      mouseBtnDown.current = false;
      dragDownPos.current = null;
      setOrbitCursor(null);
    };
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  // Force-remount key for the 3D scene/canvas to ensure a clean reload
  const [sceneReloadKey, setSceneReloadKey] = useState(0);

  // Shared camera snapshot ref: populated by CameraBridge (inside R3F) and read
  // by the path tracer, which is invoked from the same useFrame (after the snapshot) via `pathTracerFrameRef`.
  const cameraSnapshotRef = useCameraSnapshotRef();
  const pathTracerFrameRef = useRef<(() => void) | null>(null);
  const realTimeRenderActive = !!activeViewModes?.realTimeRender;

  // Real-time path tracing expects a perspective snapshot; 2D mode is hidden in the toolbar.
  useEffect(() => {
    if (realTimeRenderActive && isOrthographicView) {
      setOrthographicView(false);
    }
  }, [realTimeRenderActive, isOrthographicView, setOrthographicView]);

  // Ensure IES wireframes (with candela grids) are loaded when entering
  // real-time render mode, independent of the showIESModels toggle.
  useEffect(() => {
    if (realTimeRenderActive && activeVersion?.id) {
      dispatch(loadWireframeFiles());
    }
  }, [dispatch, realTimeRenderActive, activeVersion?.id]);
  // Tracks whether we just redirected from Exports -> Process due to incomplete calculation
  const redirectedFromExportsRef = useRef(false);

  // Spinner logic

  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [loadedModelsCount, setLoadedModelsCount] = useState(0);

  // Memoize the uploaded models to prevent unnecessary re-renders
  const memoizedModels = useMemo(() => uploadedModels, [uploadedModels]);

  // Track how many models have loaded
  useEffect(() => {
    if (memoizedModels.length > 0 && loadedModelsCount >= memoizedModels.length) {
      setModelsLoaded(true);
    } else if (!modelsLoaded) {
      setModelsLoaded(false);
    }
  }, [memoizedModels.length, loadedModelsCount, modelsLoaded]);

  // Reset loadedModelsCount when models change
  useEffect(() => {
    setLoadedModelsCount(0);
  }, [memoizedModels]);

  // Handler for when a model loads
  const handleModelLoad = useCallback(() => {
    setLoadedModelsCount((prev) => prev + 1);
  }, []);

  // Load wireframe files when showIESModels is enabled and at least one lamp has
  // an IES file or a catalogue variant assigned (market-matched versions).
  const iesFilesAvailable = activeVersion?.lights?.some((light) => !!light.iesFileId || light.variantId != null);
  useEffect(() => {
    if (showIESModels && iesFilesAvailable) {
      dispatch(loadWireframeFiles());
    }
  }, [dispatch, showIESModels, iesFilesAvailable]);

  useEffect(() => {
    if (!useFrontendMeasurement || appliedMarketMatch) return;
    dispatch(setMeasurementSource('frontend'));
  }, [dispatch, useFrontendMeasurement, appliedMarketMatch]);

  // Prefetch frontend measurement once scene models finish loading. The runner
  // defers until meshes are extractable; do not re-fire when calculating ends.
  useEffect(() => {
    const noModelsToLoad = memoizedModels.length === 0;
    const sceneReady = noModelsToLoad || modelsLoaded;
    if (!useFrontendMeasurement || !sceneReady) return;
    if (frontendMeasurementData || frontendMeasurementCalculating) return;
    requestFrontendMeasurementCalculation();
    // frontendMeasurementCalculating omitted — re-running when it flips false would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    useFrontendMeasurement,
    memoizedModels.length,
    modelsLoaded,
    loadedModelsCount,
    frontendMeasurementData,
    activeVersion?.id,
  ]);

  useEffect(() => {
    if (!projectId) return;

    // Load the project for the current projectId
    dispatch(loadProject(projectId));

    // Cleanup runs before the effect re-runs due to projectId change and on unmount
    return () => {
      dispatch(stopPeriodicChecking());
      dispatch(triggerClearWorkspaceState());
      setModelsLoaded(false);
      setLoadedModelsCount(0);
      dispatch(setCameraInitialized(false));
      setSceneReloadKey((k) => k + 1);
    };
  }, [dispatch, projectId]);

  // Ensure URL versionId controls the active version; if missing, set to last-updated version; if invalid, go 404
  useEffect(() => {
    if (!project || project.id !== projectId) return;

    const versions = project.versions || [];

    // Helper to get most recent update timestamp for a version
    const getMostRecentUpdate = (v: Version): number => {
      const dates: number[] = [];
      if (v?.updatedAt) dates.push(new Date(v.updatedAt).getTime());
      if (v?.calculationJob?.updatedAt) dates.push(new Date(v.calculationJob.updatedAt).getTime());
      if (v?.autopositioningJob?.updatedAt) dates.push(new Date(v.autopositioningJob.updatedAt).getTime());
      if (v?.renderBakingJob?.updatedAt) dates.push(new Date(v.renderBakingJob.updatedAt).getTime());
      return dates.length ? Math.max(...dates) : 0;
    };

    if (versionId) {
      const exists = versions.some((v) => v.id === versionId);
      if (exists) {
        if (project.activeVersionId !== versionId) {
          dispatch(switchActiveVersion({ projectId: project.id, versionId: versionId }));
        }
      } else {
        // Provided versionId does not exist -> 404
        history.push('/404');
      }
    } else {
      // No versionId in URL -> pick last updated version
      if (versions.length > 0) {
        const lastUpdated = versions.slice().sort((a, b) => getMostRecentUpdate(b) - getMostRecentUpdate(a))[0];
        if (lastUpdated) {
          const newParams = new URLSearchParams(searchParams);
          newParams.set('projectId', String(project.id));
          newParams.set('versionId', String(lastUpdated.id));
          setSearchParams(newParams);
          if (project.activeVersionId !== lastUpdated.id) {
            dispatch(switchActiveVersion({ projectId: project.id, versionId: lastUpdated.id }));
          }
        }
      }
    }
  }, [project, versionId, dispatch, setSearchParams, searchParams, projectId]);

  useEffect(() => {
    // Layout v2 names the open panel section in `tab` (layers, results, …); for the rest of the page
    // that is still the process tab.
    const validTab = tabParam === 'process' || tabParam === 'exports' || isPanelTab(tabParam) ? tabParam : null;
    if (!validTab) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', 'process');
      setSearchParams(newParams);
    }
  }, [tabParam, setSearchParams, searchParams]);

  useEffect(() => {
    if (activeTab === 'exports') {
      // Force re-centering on next model load/mount
      dispatch(setCameraInitialized(false));
    }
  }, [activeTab, dispatch]);

  // After redirecting from Exports -> Process due to incomplete calculation, force a scene reload
  useEffect(() => {
    if (activeTab === 'process' && redirectedFromExportsRef.current) {
      redirectedFromExportsRef.current = false; // one-shot
      // Reset scene/camera/model loading state and force remount the Canvas to ensure proper layout and camera
      setModelsLoaded(false);
      setLoadedModelsCount(0);
      dispatch(setCameraInitialized(false));
      setSceneReloadKey((k) => k + 1);
    }
  }, [activeTab, dispatch]);

  const handleExportsPanelChange = useCallback(
    (show: boolean) => {
      setShowExportsPanel(show);

      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', show ? 'exports' : 'process');
      setSearchParams(newParams);

      if (!show) {
        dispatch(setCameraInitialized(false));
      }
    },
    [dispatch, searchParams, setSearchParams],
  );

  const handleCloseExportsPanel = useCallback(() => {
    handleExportsPanelChange(false);
  }, [handleExportsPanelChange]);

  // Redirect from Exports to Process when PDF export is not available (e.g.
  // market-matched configurations that rely on frontend measurement).
  useEffect(() => {
    if (!project || !project.id || !activeVersion || !activeVersion.id) {
      return;
    }

    if ((activeTab === 'exports' || showExportsPanel) && !pdfExportEnabled) {
      redirectedFromExportsRef.current = true;
      handleExportsPanelChange(false);
    }
  }, [activeTab, showExportsPanel, project, activeVersion, pdfExportEnabled, handleExportsPanelChange]);

  useEffect(() => {
    if (project && project.activeVersionId && activeVersion) {
      dispatch(startPeriodicChecking());
    }
  }, [dispatch, project, activeVersion]);

  // Effect to check if autopositioning is in progress and dispatch startAutoPositioning
  useEffect(() => {
    if (!project || !project.id || !activeVersion) {
      return;
    }

    // Check if autopositioning is in progress
    if (checkIsAutopositioningInProgress(activeVersion)) {
      // Set isAutoPositioning to true and trigger the saga to handle SSE
      dispatch(startAutoPositioning());
    }
  }, [dispatch, project, activeVersion]);

  // Effect to check if calculation is in progress and dispatch startTamanOptimization
  useEffect(() => {
    if (!project || !project.id || !activeVersion) {
      return;
    }

    // Check if calculation is in progress
    if (checkIsCalculationInProgress(activeVersion)) {
      // Trigger the saga to handle WebSocket for calculation progress
      dispatch(startTamanOptimization());
    }
  }, [dispatch, project, activeVersion]);

  const handleUpdateLayer = useCallback(
    (uuid: string, updates: Partial<ModelLayer>) => {
      const updatedLayers = modelLayers.map((layer: ModelLayer) =>
        layer.uuid === uuid ? { ...layer, ...updates } : layer,
      );
      setModelLayers(updatedLayers);
    },
    [modelLayers, setModelLayers],
  );

  // Close sidebar on click outside (mobile only)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (window.innerWidth >= 768) return; // Only for mobile
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node) && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  return (
    <div
      className="flex flex-col text-white min-h-0 overflow-hidden"
      style={{ backgroundColor: greyP4, height: 'calc(100dvh - 64px)' }}
    >
      <PdfCacheProvider>
        <ExportProvider>
          <FrontendReportImagesProvider>
            <PdfExportProvider>
              <ViewModeTransitionProvider>
                <div className="relative flex flex-1 min-h-0 min-w-0">
                  <div className="flex-1 relative min-w-0 min-h-0" style={{ height: 'calc(100dvh - 64px)' }}>
                    {mountMeasurementCanvas && (
                      <div
                        className={
                          showProcessView
                            ? 'h-full w-full'
                            : 'absolute inset-0 h-full w-full overflow-hidden opacity-0 pointer-events-none'
                        }
                        aria-hidden={!showProcessView}
                      >
                        <Canvas
                          key={sceneReloadKey}
                          camera={{
                            position: [35, 35, 35] as [number, number, number],
                            fov: 70,
                            far: 20000,
                          }}
                        >
                          <CameraController>
                            <color attach="background" args={['#101010']} />
                            <fog attach="fog" args={['#101010', 1000, 2000]} />

                            {showProcessView && realTimeRenderActive && (
                              <CameraBridge snapshotRef={cameraSnapshotRef} afterCamera={pathTracerFrameRef} />
                            )}

                            {showProcessView && showGrid && <CustomGrid />}

                            {showProcessView && showCompass && !isOrthographicView && (
                              <GizmoHelper alignment="bottom-left" margin={[80, 80]}>
                                <GizmoViewport axisColors={['#B3EC49', '#B3EC49', '#B3EC49']} labelColor="black" />
                              </GizmoHelper>
                            )}

                            {showProcessView && <SceneInteractionHandler />}
                            <FrontendMeasurementRunner />
                            {/* Runner always computes (so the sidebar values stay current). The
                                surface visuals show ONLY in the measurement (process) view. */}
                            {measurementSurfacesEnabled && (
                              <>
                                <MeasurementPlaneRunner />
                                {showProcessView && showMeasurements && (
                                  <>
                                    <MeasurementPlanesRenderer />
                                    <MeasurementPlaneVertexEditor />
                                    <MeasurementPlaneResults />
                                    <MeasurementPlaneEditor />
                                    <MeasurementExclusionEditor />
                                  </>
                                )}
                              </>
                            )}
                            {!showMeasurements && <Scene onModelLoad={handleModelLoad} modelsLoaded={modelsLoaded} />}

                            <Lighting />

                            {/* The colored measurement model always renders in the measurement view;
                                on "Surface points" we just hide its points (model stays identical).
                                It also renders (uncolored) before results exist: the object view's
                                Scene is not mounted here, and the frontend calculation reads its
                                geometry from this model — e.g. after a refresh in the measurement view. */}
                            {showProcessView && showMeasurements && (
                              <Measurements
                                data={measurementData ?? NO_MEASUREMENT_DATA}
                                hidePoints={!showModelMeasurement}
                              />
                            )}

                            {showProcessView && <CoordinateSystem />}
                          </CameraController>
                        </Canvas>
                        {showProcessView && (
                          <>
                            <RealTimeRenderOverlay
                              active={realTimeRenderActive}
                              snapshotRef={cameraSnapshotRef}
                              pathTracerFrameRef={pathTracerFrameRef}
                            />
                            <ViewModeTransitionOverlay />
                            {showMeasurements && <FrontendMeasurementLoadingOverlay />}
                            <FileDownloadProgress
                              fileDownloadProgress={fileDownloadProgress}
                              isLoadingFiles={isLoadingFiles}
                              activeViewModes={activeViewModes}
                            />
                            {/* One legend (the whole-model scale) for the whole measurement view — surface
                                points share this SAME scale, so the colours are directly comparable. */}
                            {showMeasurements && measurementData && (
                              <MeasurementScale measurementData={measurementData} />
                            )}
                            {/* Model/surface points switch — floats above the legend (out of the sidebar),
                                only in the measurement view where both point sets are relevant. */}
                            {measurementSurfacesEnabled && showMeasurements && <MeasurementPointsSwitch />}
                            {/* Norm picker shown right after a surface is drawn (anchored at the cursor). */}
                            {measurementSurfacesEnabled && <NormPromptPopup />}
                            {layoutV2 && <ViewModeSwitch />}
                            <LeftToolbar showExportsPanel={showExportsPanel} activeViewModes={activeViewModes} />
                            {measurementSurfacesEnabled && <MeasurementPlanePanel />}
                            <BottomToolbar showExportsPanel={showExportsPanel} show2D3DToggle={!realTimeRenderActive} />
                            {/* Layout v2 shows the applied market match in the side panel's footer instead. */}
                            {!layoutV2 && <AppliedMarketMatchBadge />}
                            {/* The baked Render view can't show an applied market match; point to views that can. */}
                            {layoutV2 && <BakedRenderMarketMatchNotice />}
                            {drawingsEnabled && project?.sourceDrawingId && <EditDrawingButton project={project} />}
                            {orbitCursor && (
                              <div
                                style={{
                                  position: 'fixed',
                                  left: orbitCursor.x - 35,
                                  top: orbitCursor.y - 35,
                                  width: 70,
                                  height: 70,
                                  borderRadius: '50%',
                                  border: '3px solid rgba(255, 255, 255, 0.5)',
                                  pointerEvents: 'none',
                                  zIndex: 40,
                                }}
                              />
                            )}
                            {layerPopup &&
                              (checkIsCalculationCompleted(activeVersion) &&
                              measurementData &&
                              measurementData.some(
                                (l) =>
                                  (l.name === layerPopup.layerName ||
                                    l.name === (layerPopup.originalName || layerPopup.layerName)) &&
                                  l.actualValues &&
                                  l.actualValues.length > 0 &&
                                  l.expectedValues &&
                                  l.expectedValues.length > 0,
                              ) ? (
                                <ResultLayerPopup
                                  data={layerPopup}
                                  measurementData={measurementData}
                                  layerDisplayNames={Object.fromEntries(
                                    modelLayers.map((l) => [
                                      l.threeJsName,
                                      l.displayName || l.originalName || l.threeJsName,
                                    ]),
                                  )}
                                  normName={(() => {
                                    const layerData = measurementData?.find(
                                      (l) =>
                                        l.name === layerPopup.layerName ||
                                        (layerPopup.originalName && l.name === layerPopup.originalName),
                                    );
                                    if (layerData?.normId) return norms.find((n) => n.id === layerData.normId)?.name;
                                    const layer = modelLayers.find(
                                      (item) =>
                                        item.threeJsName === layerPopup.layerName ||
                                        item.originalName === layerPopup.originalName,
                                    );
                                    return layer?.customTargets?.length ? 'custom' : undefined;
                                  })()}
                                  onClose={() => setLayerPopup(null)}
                                />
                              ) : !checkIsCalculationCompleted(activeVersion) ? (
                                <LayerQuickEditPopup data={layerPopup} onClose={() => setLayerPopup(null)} />
                              ) : null)}
                            {lampPopup && <LampQuickEditPopup data={lampPopup} onClose={() => setLampPopup(null)} />}
                          </>
                        )}
                      </div>
                    )}
                    {exportVisible && (
                      <div className="absolute inset-0 z-10">
                        <Suspense
                          fallback={
                            <div className="flex items-center justify-center h-full">Loading PDF Export...</div>
                          }
                        >
                          <NewExportView onClose={handleCloseExportsPanel} />
                        </Suspense>
                      </div>
                    )}
                  </div>

                  {layoutV2 ? (
                    <WorkspaceSidePanel exportsOpen={exportVisible} onExportsChange={handleExportsPanelChange} />
                  ) : (
                    /* Sidebar - on the right side */
                    <div className="relative flex-shrink-0 min-h-0" ref={sidebarRef}>
                      {/* Chevron toggle button */}
                      <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="absolute left-0 top-[10%] -translate-x-full -translate-y-[10%] md:top-1/2 md:-translate-y-1/2 z-10 bg-greyP5 hover:bg-greyP4 p-1 rounded-l-md border border-r-0 border-greyP5"
                        aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
                      >
                        {sidebarOpen ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                      </button>
                      {sidebarOpen && (
                        <div
                          className="flex flex-col w-[calc(100vw-48px)] md:w-[455px] min-h-0 overflow-hidden"
                          style={{ height: 'calc(100dvh - 64px)' }}
                        >
                          <Sidebar
                            isOpen={true}
                            showExportsPanel={showExportsPanel || activeTab === 'exports'}
                            onExportsPanelChange={handleExportsPanelChange}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </ViewModeTransitionProvider>
            </PdfExportProvider>
          </FrontendReportImagesProvider>
        </ExportProvider>
      </PdfCacheProvider>

      <AutoPositionSidebar
        isOpen={showAutoPositionSidebar}
        onClose={() => setShowAutoPositionSidebar(false)}
        modelLayers={modelLayers}
        onUpdateLayer={handleUpdateLayer}
      />
    </div>
  );
};
